import APITemplate from '@api/APITemplate'
import { diffTimeMinutes, formatQueryParams } from '@utils/Format'
import store from '@state/store'
import { MINUTES_TIMEOUT_REQUESTS } from '@constants/Numbers'
import con from '@services/connection'
import { crm } from '@modules/crm/constants/URL'
import { GlobalBus } from '@services/bus'
import { RELOAD } from '@constants/Text'

class APICrmLeads extends APITemplate {
  constructor() {
    super()

    this.MODULE = 'crm'
    this.reqCarregar = null

    // STORE KEYS
    this.KEY_DATA = `${this.MODULE}/leads`
    this.KEY_LOADING = `${this.MODULE}/leadsLoading`
    this.KEY_LAST_UPDATE = `${this.MODULE}/leadsLastUpdate`

    // STORE ACTIONS
    this.ACTION_SET_DATA = `${this.MODULE}/setLeads`
    this.ACTION_SET_LOADING = `${this.MODULE}/setLeadsLoading`
    this.ACTION_ADD = `${this.MODULE}/addLeads`
    this.ACTION_SET_TAGS = `${this.MODULE}/setLeadsTags`
  }

  // ---------- HELPERS ----------
  shouldUseCache(forceUpdate) {
    if (forceUpdate) return false

    return !diffTimeMinutes(
      store.getters[this.KEY_LAST_UPDATE],
      MINUTES_TIMEOUT_REQUESTS
    )
  }

  setDataFromResponse(r) {
    store.dispatch(this.ACTION_SET_DATA, (r && r.value) || [])
  }

  async getEarlyResponse(forceUpdate) {
    if (this.reqCarregar) {
      const r = await this.reqCarregar
      return (
        r ||
        this.buildErrorResponse({
          message: 'Erro ao carregar dados',
        })
      )
    }

    if (this.shouldUseCache(forceUpdate)) {
      return this.buildSuccessResponse({
        message: 'Dados carregados da memória',
        value: store.getters[this.KEY_DATA] || [],
      })
    }

    if (store.getters[this.KEY_LOADING]) {
      return this.buildSuccessResponse({
        message: 'Requisição em andamento, dados carregados da memória',
        value: store.getters[this.KEY_DATA] || [],
      })
    }

    return null
  }

  resolveUrl(filters) {
    const params = {}
    if (filters) {
      params.filters = JSON.stringify(filters)
    }

    return crm.leads() + formatQueryParams(params)
  }

  // ---------- REQUESTS ----------
  async carregar({ forceUpdate = false, filters = {} } = {}) {
    const earlyResponse = await this.getEarlyResponse(forceUpdate)
    if (earlyResponse) return earlyResponse

    store.dispatch(this.ACTION_SET_LOADING, true)

    this.reqCarregar = con.get(this.resolveUrl(filters))

    let response = await this.reqCarregar

    this.setDataFromResponse(response)

    store.dispatch(this.ACTION_SET_LOADING, false)

    this.reqCarregar = null

    return (
      response ||
      this.buildErrorResponse({
        message: 'Erro ao carregar dados',
      })
    )
  }

  // Busca filtrada SEM cache/dedupe/store: `carregar` responde pelo store da
  // tela de leads ignorando `filters` e gravaria o recorte no `crm/leads` global.
  async buscarLista({ filters = {} } = {}) {
    const response = await con.get(this.resolveUrl(filters))
    return (
      response ||
      this.buildErrorResponse({
        message: 'Erro ao carregar dados',
      })
    )
  }

  async salvar({
    id,
    nome,
    email,
    cpf,
    telefone,
    id_cliente_indicador,
    id_cliente_app,
    id_profissional_responsavel,
    valor_estimado,
    origem,
    id_origem,
    status,
    descricao,
    tags,
    id_funil_vendas_etapa_atual,
    if_funil_vendas, // Sim, é isso mesmo
    id_funil_vendas,
    data_fechamento_prevista,
    temperatura,
    campos_personalizados,
  }) {
    const dado = {
      id_cliente_indicador,
      id_cliente_app,
      id_profissional_responsavel,
      valor_estimado,
      origem,
      id_origem,
      status,
      descricao,
      tags,
      id_funil_vendas_etapa_atual,
      if_funil_vendas,
      id_funil_vendas,
      data_fechamento_prevista,
      temperatura,
      campos_personalizados,
    }

    if (!id_cliente_app) {
      dado.cliente = {
        email,
        cpf,
        nome,
        telefone,
      }
    }

    const data = {
      data: JSON.stringify(dado),
    }

    let r
    if (id && id.length) {
      r = await con.put(crm.leads(id), data)
    } else {
      r = await con.post(crm.leads(id), data)
    }

    if (r && r.status === 200) {
      const value = r.value[0] || r.value || {}
      store.dispatch(this.ACTION_ADD, value)
      GlobalBus.$emit(RELOAD.CRM_RELATIONSHIP_NEW_LEAD, value)
    }

    return (
      r ||
      this.buildErrorResponse({
        message: 'Erro ao salvar lead.',
      })
    )
  }

  async carregarTags(force) {
    if (force) {
      const params = {
        only_tags: true,
      }

      const r = await con.get(crm.leadsOverview + formatQueryParams(params))

      if (r && r.status === 200) {
        const value = r.value || {}
        store.dispatch(this.ACTION_SET_TAGS, value.tags_disponiveis || [])
      } else {
        store.dispatch(this.ACTION_SET_TAGS, [])
      }

      return (
        r ||
        this.buildErrorResponse({
          message: 'Erro ao carregar tags.',
        })
      )
    }
  }
}

const apiCrmLeads = new APICrmLeads()
export { apiCrmLeads }
